import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import 'mantine-react-table/styles.css';

import { ActionsColumn, IdColumn, NameColumn, StatusColumn } from './columns';
import {
    Box,
    Container,
    Flex,
    Group,
    MultiSelect,
    Portal,
    Select,
    Text,
} from '@mantine/core';
import {
    useDebouncedCallback,
    useDisclosure,
    useLocalStorage,
} from '@mantine/hooks';
import { useMemo, useState } from 'react';

import ConversationViewModal from '../modals/ConversationViewModal';
import FiltersMultiSelect from '../../../common/filtersMultiSelect/FiltersMultiSelect';
import { MantineReactTable } from 'mantine-react-table';
import { StatColumn } from '../../../common';
import { getConversations } from '../../../../http/apiClient';
import { sumObjectKeys } from '../../../../utils/statUtils';
import { usePaginatedQuery } from '../../../../http/queries/usePaginatedQuery';

const ConversationListView = ({ ...props }) => {
    const [globalFilter, setGlobalFilter] = useState(null);
    const [sorting, setSorting] = useState([]);
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 50,
    });

    const filterDebounce = useDebouncedCallback((query) => {
        setGlobalFilter(query);
    }, 600);

    const {
        isLoading,
        isError,
        error,
        data: paginatedData,
    } = usePaginatedQuery({
        queryFn: (queryParams) => getConversations(queryParams),
        queryKey: ['conversations'],
        refetchInterval: 15_0000,
        globalFilter,
        pagination,
        sorting,
    });

    const [
        conversationModalOpen,
        { open: openConversationModal, close: closeConversationModal },
    ] = useDisclosure();

    const [modalConversation, setModalConversation] = useState(null);

    const { data: conversations, pagination: dataPagination } =
        paginatedData || {};
    const columns = useMemo(
        () => [
            IdColumn(),
            NameColumn(),
            StatusColumn(),

            StatColumn('Clicks', (x) =>
                sumObjectKeys(x.stats, ['uniqueClick', 'uniquePreview'])
            ),
            StatColumn('Sent', (x) =>
                sumObjectKeys(x.stats, [
                    'send.initial',
                    'send.response',
                    'send.keepAlive',
                ])
            ),
            StatColumn('Received', (x) => x.stats?.receive),
            StatColumn('Links', (x) => sumObjectKeys(x.stats, ['send.link'])),
            StatColumn('Keep-Alives', (x) =>
                sumObjectKeys(x.stats, ['send.keepAlive'])
            ),
        ],
        [conversations, isLoading]
    );

    const tableData = useMemo(
        () => (isLoading ? [] : conversations),
        [conversations, isLoading]
    );

    const [columnVisibility, setColumnVisibility] = useLocalStorage({
        key: 'conversations-table-visibility', // a unique key for local storage
        defaultValue: {}, // default to all columns shown if empty
        getInitialValueInEffect: true,
    });

    const [columnSizing, setColumnSizing] = useLocalStorage({
        key: 'conversations-group-table-sizing', // a unique key for local storage
        defaultValue: {}, // default to all columns shown if empty
        getInitialValueInEffect: true,
    });

    const handleVisibilityChange = (changeFunc) => {
        const changes = changeFunc();
        setColumnVisibility((prevState) => ({
            ...prevState,
            ...changes,
        }));
    };

    const handlSizeChange = (changeFunc) => {
        const changes = changeFunc();
        setColumnSizing((prevState) => ({
            ...prevState,
            ...changes,
        }));
    };

    const handleFilterChange = (queryStr) => {
        filterDebounce(queryStr);
    };

    const tableProps = {
        columns: columns,
        data: tableData,
        enableRowActions: true,
        enableColumnResizing: true,
        enableColumnActions: false,
        enableSorting: false,
        enableTopToolbar: true,
        enableGlobalFilter: true,
        enableFilters: false,
        enableStickyHeader: true,
        enableDensityToggle: false,
        positionActionsColumn: 'last',
        layoutMode: 'semantic',
        mantineSearchTextInputProps: {
            placeholder: 'Search Conversations',
            miw: 200,
        },
        mantineTableContainerProps: {
            style: {
                minHeight: '80vh',
            },
        },
        positionGlobalFilter: 'left',
        renderTopToolbarCustomActions: () => (
            <Box
                w={'100%'}
                px={'lg'}>
                <FiltersMultiSelect
                    options={[{ label: 'test', value: 'test' }]}
                />
            </Box>
        ),
        renderRowActions: (props) => (
            <ActionsColumn
                {...props}
                setModalItem={setModalConversation}
                openConversationModal={openConversationModal}
            />
        ),

        manualPagination: true,
        manualSorting: true,
        onColumnVisibilityChange: handleVisibilityChange,
        onColumnSizingChange: handlSizeChange,
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        onGlobalFilterChange: handleFilterChange,
        rowCount: dataPagination?.totalItems || 0,
        pageCount: dataPagination?.totalPages || 1,

        initialState: {
            columnVisibility,
            pagination,
            isLoading,
            showGlobalFilter: true,
        },
        state: {
            isLoading,
            pagination,
            columnVisibility,
            columnSizing,
            showAlertBanner: isError,
            showProgressBars: isLoading,
            sorting,
            density: 'xs',
        },
    };

    return (
        <Container fluid>
            <MantineReactTable {...tableProps} />
            <ConversationViewModal
                conversationId={modalConversation?.id}
                opened={conversationModalOpen}
                close={closeConversationModal}
            />
        </Container>
    );
};
export default ConversationListView;
